import './ChargerDetail.css';
import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
// Components
import ChargingHistory from './components/ChargingHistory';
import ChargerChart from './components/ChargerChart';
import ChargerUpdateSideModal from './components/ChargerUpdateSideModal';
import StationSearchModal from './components/StationSearchModal';
import ChargerDeleteModal from '../ChargerManagement/component/ChargerDeleteModal';
// Icons
import EditIcon from '../../assets/icons/edit.svg?react';
import DeleteIcon from '../../assets/icons/delete.svg?react';
import ChevronLeftIcon from '../../assets/icons/chevron-left.svg?react';
// Hooks
import { useChargerDetail } from '../../hooks/useChargerDetail';
// React Query
import { useQueryClient } from '@tanstack/react-query';

const ChargerDetail = () => {
  // Params
  const { chargerSysId } = useParams();
  // Navigate
  const navigate = useNavigate();
  const location = useLocation();
  // States
  const [activeTab, setActiveTab] = useState('stat');
  const [chargerUpdateSideModalOn, setChargerUpdateSideModalOn] = useState(false);
  const [stationSearchModalOn, setStationSearchModalOn] = useState(false);
  const [selectedStation, setSelectedStation] = useState(null);
  const [deleteModalOn, setDeleteModalOn] = useState(false);
  // React Query Client
  const queryClient = useQueryClient();

  // 뒤로가기 핸들러
  const handleBack = () => {
    if (location.state?.from) {
      navigate(-1); // 프로젝트 내에서 접근한 경우 뒤로가기
    } else {
      navigate('/charger-management'); // URL로 직접 접근한 경우 목록으로
    }
  };
  // React Query Hooks
  const {
    charger,
    station,
    chargingHistoryTimeSlot,
    periodChargerStatTwoWeeks,
    periodChargerStatOneYear,
    drResource,
    isLoading,
    error,
  } = useChargerDetail(chargerSysId);

  // Effect
  useEffect(() => {
    if(station){
      setSelectedStation(station);
    }
  }, [station]);

  // Success Handlers
  const onEditSuccess = () => {
    // 충전기 관련 쿼리 캐시 무효화하여 데이터 다시 불러오기
    queryClient.invalidateQueries({ queryKey: ['chargers', 'detail'] });
  };

  const onDeleteSuccess = () => {
    // 삭제 성공 시 목록 페이지로 이동
    navigate('/charger-management');
  };

  // Loading
  if (isLoading) {
    return (
      <div className="detail-loading">
        <div className="loading-spinner"></div>
        <p className="text-b1-400 text-base">충전기 정보를 불러오는 중...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="detail-error">
        <h2 className="text-b1-700 text-base">오류가 발생했습니다</h2>
        
        <button onClick={() => navigate('/charger-management')} className="btn big-btn btn-style-5">
          목록으로 돌아가기
        </button>
      </div>
    );
  }

  if (!charger) {
    return (
      <div className="detail-not-found">
        <h2 className="text-b1-700 text-base">충전기를 찾을 수 없습니다</h2>
        <p className="text-b1-400 text-base">요청하신 충전기가 존재하지 않거나 삭제되었습니다.</p>
        <button onClick={() => navigate('/charger-management')} className="btn big-btn btn-primary">
          목록으로 돌아가기
        </button>
      </div>
    );
  }

  return (
    <div className="detail-page-container">
      <div className="detail-page-content">
          <div className='detail-page-content-divide-container'>
            <div className='detail-page-content-divide-content left'>
              {/* LEFT HEADER */}
              <div className='flex align-center mb-5'>
                <button className='btn icon-btn' onClick={handleBack} title="뒤로가기">
                  <ChevronLeftIcon color='var(--gray-600)'/>
                  <span className='text-b2-700 text-gray-600'>뒤로가기</span>
                </button>
              </div>
              <div className='detail-page-content-divide-content-header flex align-center gap-5'>
                <h3 className='text-b1-700 text-primary'>{`${station?.CpoStationId ? station?.CpoStationId : ""}-${charger?.CpoChargerId ? charger?.CpoChargerId : ""}`}</h3>
                <button className='btn icon-btn'>
                  <EditIcon color='var(--gray-600)' onClick={() => setChargerUpdateSideModalOn(true)}/>
                </button>
                <button className='btn icon-btn' onClick={() => setDeleteModalOn(true)}>
                  <DeleteIcon color='var(--gray-600)'/>
                </button>
              </div>
              {/* LEFT CONTENT */}
              <div className='flex flex-col gap-10 mt-10 mb-20'>
                <h3 className='text-b1-700 text-base'>기본 정보</h3>
                <table className='gray-table'>
                  <tr>
                    <th width="180px">고유식별자</th>
                    <td>{charger?.CpoChargerId}</td>
                  </tr>
                  <tr>
                    <th width="180px">용량(kWh)</th>
                    <td>{charger?.ChargerCapacity}</td>
                  </tr>
                </table>
              </div>
              
              <div className='flex flex-col gap-10 mt-10 mb-20'>
                <div className='flex align-center justify-between'>
                  <h3 className='text-b1-700 text-base'>충전소 정보</h3>
                  {station?.StationSysId && (
                  <p className='text-b2-400 text-gray-600 cursor-pointer text-underline' onClick={() => {navigate(`/station-management/detail/${station?.StationSysId}`, { state: { from: 'detail' } })}}>자세히보기</p>
                  )}
                </div>
                <table className='gray-table'>
                  <tr>
                    <th width="180px">이름</th>
                    <td>{station?.CpoStationName}</td>
                  </tr>
                  <tr>
                    <th width="180px">고유 식별자</th>
                    <td>{station?.CpoStationId}</td>
                  </tr>
                  <tr>
                    <th width="180px">주소</th>
                    <td>{station?.Address}</td>
                  </tr>
                  <tr>
                    <th width="180px">계량기 계약번호</th>
                    <td>{station?.PowerMeterContractNumber}</td>
                  </tr>
                </table>
              </div>
              
              <div className='flex flex-col gap-10 mt-10 mb-20'>
                <div className='flex align-center justify-between'>
                <h3 className='text-b1-700 text-base'>DR자원 정보</h3>
                  {charger?.DrResourceSysId && (
                  <p className='text-b2-400 text-gray-600 cursor-pointer text-underline' onClick={() => {navigate(`/dr-management/detail/${station?.DrResourceSysId}`, { state: { from: 'detail' } })}}>자세히보기</p>
                  )}
                </div>
                <table className='gray-table'>
                  <tr>
                    <th width="180px">DR자원 이름</th>
                    <td>{drResource?.ResourceName || "미할당"}</td>
                  </tr>
                </table>
              </div>
              

            </div>
            <div className='detail-page-content-divide-content right'>
              {/* RIGHT CONTENT */}
              <div className="charger-detail-tabs-container">
                <div className="tabs charger-detail-tabs">
                  <button
                    className={`tab ${activeTab === 'stat' ? 'active' : ''}`}
                    onClick={() => setActiveTab('stat')}
                  >
                    통계
                  </button>
                  <button
                    className={`tab ${activeTab === 'history' ? 'active' : ''}`}
                    onClick={() => setActiveTab('history')}
                  >
                    충전이력
                  </button>
                </div>
              </div>
              {activeTab === 'stat' && (
                <>
                  <ChargerChart
                    chargingHistoryTimeSlot={chargingHistoryTimeSlot}
                    periodChargerStatTwoWeeks={periodChargerStatTwoWeeks}
                    periodChargerStatOneYear={periodChargerStatOneYear}
                    isLoading={isLoading}
                    error={error}
                  />
                </>
              )}
              {activeTab === 'history' && (
                <>
                  <ChargingHistory chargerSysId={chargerSysId} />
                </>
              )}
            </div>
          </div>
        </div>
        {chargerUpdateSideModalOn && <ChargerUpdateSideModal onClose={() => setChargerUpdateSideModalOn(false)} onSuccess={onEditSuccess} target={{charger: charger, stationInfo: station}}  onStationSearch={() => setStationSearchModalOn(true)} selectedStation={selectedStation} setSelectedStation={setSelectedStation} />}
        {stationSearchModalOn && <StationSearchModal onClose={() => setStationSearchModalOn(false)} selectedStation={selectedStation} onSelect={setSelectedStation} />}
        {deleteModalOn && <ChargerDeleteModal onClose={() => setDeleteModalOn(false)} charger={charger} stationInfo={station} onDeleteSuccess={onDeleteSuccess} />}
    </div>
  );
};

export default ChargerDetail;
